import React, {useCallback, useEffect, useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    InputAdornment,
    Pagination,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {Link as RouterLink, useLocation, useNavigate} from 'react-router';
import {DEFAULT_PAGE_SIZE, useEntities} from '../../hooks/useEntities';
import {DEFAULT_MENTIONS_PAGE_SIZE, useEntityMentionsList} from '../../hooks/useEntityMentionsList';
import {DEFAULT_GROUPED_MENTIONS_PAGE_SIZE, useGroupedMentions} from '../../hooks/useGroupedMentions';
import {useEntityTierCounts} from '../../hooks/useEntityTierCounts';
import {useDebounce} from '../../hooks/useDebounce';
import {getEntityTypeLabel, getEntityTypeMeta, getEntityTypeDesc} from '../../utils/entityTypeMeta';
import CreateCustomEntityDialog from './components/CreateCustomEntityDialog';

// ─── Tab ids ──────────────────────────────────────────────────────────────────

const TABS = [
    {id: 'approved',   label: 'Approved',          icon: <CheckCircleOutlineIcon fontSize="small"/>},
    {id: 'candidates', label: 'Candidates',         icon: <HelpOutlineIcon fontSize="small"/>},
    {id: 'mentions',   label: 'Mentions',           icon: <TextFieldsIcon fontSize="small"/>},
    {id: 'no-qid',     label: 'Custom Entities',  icon: <LinkOffIcon fontSize="small"/>},
];

function tabIndexFromHash(hash) {
    const id = hash.replace('#', '');
    const idx = TABS.findIndex(t => t.id === id);
    return idx >= 0 ? idx : 0;
}

// ─── Source filter config ─────────────────────────────────────────────────────

const DOC_SOURCE_TYPE_LABELS = {
    PDF:   'PDF',
    AUDIO: 'Audio',
    VIDEO: 'Video',
    OSINT: 'Web',
    FINTEL: 'FINTEL',
};
const DOC_SOURCE_TYPE_COLORS = {
    PDF:   'error',
    AUDIO: 'secondary',
    VIDEO: 'info',
    OSINT: 'primary',
    FINTEL: 'success',
};
const DOC_SOURCE_TYPES = ['FINTEL', 'PDF', 'OSINT', 'AUDIO', 'VIDEO'];

const getDocSourceLabel = (t) => DOC_SOURCE_TYPE_LABELS[t] || t;
const getDocSourceColor = (t) => DOC_SOURCE_TYPE_COLORS[t] || 'default';

// ─── Entity-type config ───────────────────────────────────────────────────────

// Entity-type color is driven by the shared TYPE_META palette (entityTypeMeta.js).
// Each type carries its own hex color; we return it as a CSS value for sx props,
// and also expose a helper that returns the MUI color name where it maps cleanly
// (used for Chip color prop which only accepts theme palette keys).
const getEntityTypeColor = (type) => {
    const meta = getEntityTypeMeta(type);
    return meta.color;
};

// MUI Chip `color` prop only accepts theme keys. We fall back to 'default' for
// unknown types and map the known ones explicitly so chips render with proper
// MUI variant styling (border, background) instead of custom hex.
const CHIP_COLOR_MAP = {
    GPE:     'info',
    LOC:     'info',
    ORG:     'success',
    PERSON:  'secondary',
    PER:     'secondary',
    EVENT:   'warning',
    FAC:     'primary',
    NORP:    'default',
    PRODUCT: 'default',
    LAW:     'secondary',
    DATE:    'default',
    TIME:    'default',
    MONEY:   'default',
    QUANTITY:'default',
};
const getEntityTypeChipColor = (type) => (type && CHIP_COLOR_MAP[type.toUpperCase()]) || 'default';

// ─── Report-type config ───────────────────────────────────────────────────────

const REPORT_TYPE_LABELS = {
    fintel:           'FINTEL',
    draft:            'Draft',
    analytic:         'Analytic',
    tactical_sitrep:  'Tactical SITREP',
    strategic_sitrep: 'Strategic SITREP',
    pitch:            'Pitch',
    disclosure:       'Disclosure',
    ANALYTIC:         'FINTEL',
    FINTEL:           'FINTEL',
    SITREP:           'SITREP',
    PITCH:            'Pitch',
    REPORT:           'Report',
    DISCLOSURE:       'Disclosure',
};

const REPORT_TYPE_CHIP_COLORS = {
    fintel:           'success',
    draft:            'primary',
    analytic:         'primary',
    tactical_sitrep:  'warning',
    strategic_sitrep: 'error',
    pitch:            'secondary',
    disclosure:       'info',
    ANALYTIC:         'success',
    FINTEL:           'success',
    SITREP:           'warning',
    PITCH:            'secondary',
    REPORT:           'default',
    DISCLOSURE:       'info',
};

const getReportTypeLabel = (type) => REPORT_TYPE_LABELS[type] || type;
const getReportTypeColor = (type) => REPORT_TYPE_CHIP_COLORS[type] || 'default';

// ─── Shared sub-components ────────────────────────────────────────────────────

/** Inline count badge on tab labels. */
const TabCount = ({count, loading}) => {
    if (loading) return (
        <Box component="span" sx={{ml: 0.75}}>
            <CircularProgress size={10} thickness={5}/>
        </Box>
    );
    if (!count) return null;
    return (
        <Box
            component="span"
            sx={{
                ml: 0.75, px: 0.7, py: 0,
                fontSize: '0.62rem', fontWeight: 700, lineHeight: 1.8,
                borderRadius: '999px',
                bgcolor: 'action.selected',
                display: 'inline-flex', alignItems: 'center',
            }}
        >
            {count.toLocaleString()}
        </Box>
    );
};

/** Filter bar: search + entity-type chips. Shared across Approved and Candidates tabs. */
const EntityFilterBar = ({search, onSearchChange, selectedType, onTypeChange, entityTypes}) => (
    <Stack spacing={1.5}>
        <TextField
            placeholder="Search entities..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            size="small"
            slotProps={{
                input: {startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment>},
            }}
            sx={{maxWidth: 400}}
        />
        {entityTypes.length > 0 && (
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                <Chip
                    size="small"
                    label="All types"
                    variant={!selectedType ? 'filled' : 'outlined'}
                    onClick={() => onTypeChange('')}
                />
                {entityTypes.map(type => (
                    <Tooltip key={type} title={getEntityTypeDesc(type) || type} arrow placement="top">
                        <Chip
                            size="small"
                            label={getEntityTypeLabel(type) || type}
                            variant={selectedType === type ? 'filled' : 'outlined'}
                            color={getEntityTypeChipColor(type)}
                            onClick={() => onTypeChange(type === selectedType ? '' : type)}
                        />
                    </Tooltip>
                ))}
            </Stack>
        )}
    </Stack>
);

/** Pagination row shown below any list. Hidden while a new page is loading so
 *  stale counts from the previous type/filter do not remain on screen. */
const ListPaginator = ({page, totalPages, totalElements, pageSize, onChange, loading}) => {
    if (loading || totalPages <= 1) return null;
    const first = page * pageSize + 1;
    const last  = Math.min((page + 1) * pageSize, totalElements);
    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} pt={1}>
            <Typography variant="body2" color="text.secondary">
                Showing {first.toLocaleString()}–{last.toLocaleString()} of {totalElements.toLocaleString()}
            </Typography>
            <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, p) => { onChange(p - 1); window.scrollTo({top: 0, behavior: 'smooth'}); }}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
            />
        </Stack>
    );
};

// ─── Manual-link chip ─────────────────────────────────────────────────────────

/** Small badge marking an entity/mention that was linked to Wikidata by a human. */
const ManualLinkChip = ({manuallyLinked}) => {
    if (!manuallyLinked) return null;
    return (
        <Tooltip title="Linked to Wikidata manually by a user" arrow placement="top">
            <Chip
                icon={<LinkIcon sx={{fontSize: 14}}/>}
                label="Manually linked"
                size="small"
                color="secondary"
                variant="outlined"
                sx={{flexShrink: 0, height: 22, '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem'}}}
            />
        </Tooltip>
    );
};

/** Badge for custom (D…) entities without a Wikidata ID. */
const ChartLocalChip = ({chartLocal}) => {
    if (!chartLocal) return null;
    return (
        <Tooltip title="User-created entity without a Wikidata ID" arrow placement="top">
            <Chip
                label="Custom"
                size="small"
                color="default"
                variant="outlined"
                sx={{flexShrink: 0, height: 22, '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem'}}}
            />
        </Tooltip>
    );
};

// ─── Frequency badge ──────────────────────────────────────────────────────────

const FreqBadge = ({occurrenceCount, documentCount, reportCount}) => {
    const occ  = Number(occurrenceCount) || 0;
    const docs = Number(documentCount)   || 0;
    const reps = Number(reportCount)     || 0;
    if (occ === 0 && docs === 0) return null;
    const pill = (label, color, title) => (
        <Tooltip key={label} title={title} arrow placement="top">
            <Box sx={{
                px: 0.8, py: 0,
                fontSize: '0.67rem', fontWeight: 700, lineHeight: 1.85,
                borderRadius: '999px',
                border: '1px solid',
                borderColor: color === 'info' ? 'info.light' : 'divider',
                color: color === 'info' ? 'info.main' : 'text.secondary',
                whiteSpace: 'nowrap', flexShrink: 0,
            }}>
                {label}
            </Box>
        </Tooltip>
    );
    return (
        <Stack direction="row" gap={0.5} alignItems="center" flexShrink={0}>
            {occ  > 0 && pill(`${occ.toLocaleString()}×`,  'neutral', `${occ.toLocaleString()} total NER occurrences`)}
            {docs > 0 && pill(`${docs.toLocaleString()} doc${docs !== 1 ? 's' : ''}`, 'neutral', `${docs.toLocaleString()} distinct document${docs !== 1 ? 's' : ''}`)}
            {reps > 0 && pill(`${reps.toLocaleString()} rpt${reps !== 1 ? 's' : ''}`, 'info',    `Referenced in ${reps.toLocaleString()} report${reps !== 1 ? 's' : ''}`)}
        </Stack>
    );
};

// ─── Shared sources filter bar ────────────────────────────────────────────────

const SourcesFilterBar = ({
    docSourceType, inDataItems, inReports, inCorpus,
    onDocSourceType, onInDataItems, onInReports, onInCorpus, onReset,
    showInReports = true,
}) => {
    const hasSourceFilter = docSourceType || inDataItems || inReports || inCorpus;
    return (
        <Stack spacing={0.75}>
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{fontWeight: 700, letterSpacing: 0.8, mr: 0.5, whiteSpace: 'nowrap'}}
                >
                    SOURCES
                </Typography>
                <Chip
                    size="small"
                    label="All sources"
                    variant={!hasSourceFilter ? 'filled' : 'outlined'}
                    onClick={onReset}
                />
                <Tooltip title="Entities linked inside data-library items" arrow placement="top">
                    <Chip
                        size="small"
                        label="Data Items"
                        variant={inDataItems ? 'filled' : 'outlined'}
                        color="secondary"
                        onClick={onInDataItems}
                    />
                </Tooltip>
                {showInReports && (
                    <Tooltip title="Entities appearing in at least one report" arrow placement="top">
                        <Chip
                            size="small"
                            label="In Reports"
                            variant={inReports ? 'filled' : 'outlined'}
                            color="success"
                            onClick={onInReports}
                        />
                    </Tooltip>
                )}
                <Tooltip title="Entities found in ingested corpus documents via NER" arrow placement="top">
                    <Chip
                        size="small"
                        label="Corpus"
                        variant={inCorpus ? 'filled' : 'outlined'}
                        color="info"
                        onClick={onInCorpus}
                    />
                </Tooltip>
            </Stack>

            {(inDataItems || inCorpus) && (
                <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center" sx={{pl: 1.5}}>
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{fontWeight: 600, fontSize: '0.65rem', letterSpacing: 0.5, whiteSpace: 'nowrap'}}
                    >
                        type:
                    </Typography>
                    <Chip
                        size="small"
                        label="All types"
                        variant={!docSourceType ? 'filled' : 'outlined'}
                        color={inCorpus ? 'info' : 'secondary'}
                        onClick={() => onDocSourceType('')}
                    />
                    {DOC_SOURCE_TYPES.map(st => (
                        <Tooltip
                            key={st}
                            title={`${inCorpus ? 'Corpus documents' : 'Data items'} of type ${getDocSourceLabel(st)}`}
                            arrow
                            placement="top"
                        >
                            <Chip
                                size="small"
                                label={getDocSourceLabel(st)}
                                variant={docSourceType === st ? 'filled' : 'outlined'}
                                color={getDocSourceColor(st)}
                                onClick={() => onDocSourceType(docSourceType === st ? '' : st)}
                            />
                        </Tooltip>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

// ─── Hook for source-filter state (shared across tabs) ───────────────────────

function useSourceFilters() {
    const [docSourceType, setDocSourceType] = useState('');
    const [inDataItems, setInDataItems]     = useState(false);
    const [inReports, setInReports]         = useState(false);
    const [inCorpus, setInCorpus]           = useState(false);

    const reset = useCallback(() => {
        setDocSourceType('');
        setInDataItems(false);
        setInReports(false);
        setInCorpus(false);
    }, []);

    const handleInDataItems = useCallback(() => {
        setInDataItems(v => !v);
        setDocSourceType('');
        setInReports(false);
        setInCorpus(false);
    }, []);

    const handleInReports = useCallback(() => {
        setInReports(v => !v);
        setDocSourceType('');
        setInDataItems(false);
        setInCorpus(false);
    }, []);

    const handleInCorpus = useCallback(() => {
        setInCorpus(v => !v);
        setDocSourceType('');
        setInDataItems(false);
        setInReports(false);
    }, []);

    const handleDocSourceType = useCallback((v) => {
        setDocSourceType(v);
    }, []);

    return {
        docSourceType, inDataItems, inReports, inCorpus,
        handlers: {
            onDocSourceType: handleDocSourceType,
            onInDataItems: handleInDataItems,
            onInReports: handleInReports,
            onInCorpus: handleInCorpus,
            onReset: reset,
        },
    };
}

// ─── Approved / Candidates entity row ────────────────────────────────────────

const EntityRow = ({entity}) => {
    const hasReports = entity.reportCount > 0;
    const typeEntries = Object.entries(entity.reportTypeCounts || {});
    const aliases = Array.isArray(entity.aliases) ? entity.aliases.filter(Boolean) : [];
    const mentionTexts = Array.isArray(entity.mentionTexts) ? entity.mentionTexts.filter(Boolean) : [];

    // Dedupe: surface forms that differ from the canonical name and from each other
    const canonicalLower = (entity.canonicalName || '').toLowerCase();
    const uniqueMentions = [...new Set(
        mentionTexts.filter(t => t.toLowerCase() !== canonicalLower)
    )];

    // Wikidata aliases that aren't already covered by the surface forms
    const mentionSet = new Set(uniqueMentions.map(t => t.toLowerCase()));
    const extraAliases = aliases.filter(
        a => a && a.toLowerCase() !== canonicalLower && !mentionSet.has(a.toLowerCase())
    );

    const MAX_SURFACE = 5;
    const visibleMentions  = uniqueMentions.slice(0, MAX_SURFACE);
    const hiddenMentions   = uniqueMentions.length - visibleMentions.length;

    const MAX_ALIAS = 3;
    const visibleAliases   = extraAliases.slice(0, MAX_ALIAS);
    const hiddenAliases    = extraAliases.length - visibleAliases.length;

    return (
        <Box
            component={RouterLink}
            to={`/kb/${entity.id}`}
            sx={{
                display: 'flex', alignItems: 'center', gap: 2, p: 2,
                textDecoration: 'none', color: 'inherit',
                borderLeft: '3px solid',
                borderLeftColor: hasReports ? 'info.main' : 'transparent',
                transition: 'background-color 0.15s',
                '&:hover': {bgcolor: 'action.hover'},
            }}
        >
            <Avatar
                src={entity.image || entity.imageUrl || undefined}
                sx={{flexShrink: 0, bgcolor: getEntityTypeColor(entity.entityType)}}
            >
                {!entity.image && !entity.imageUrl && (entity.canonicalName || '?')[0].toUpperCase()}
            </Avatar>

            <Stack sx={{flex: 1, minWidth: 0}}>
                <Typography fontWeight={600}>{entity.canonicalName}</Typography>

                {entity.description && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {entity.description}
                    </Typography>
                )}

                {/* Surface forms: how this entity actually appears in your corpus */}
                {visibleMentions.length > 0 && (
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{mt: 0.5}}>
                        <Typography variant="caption" color="text.disabled" sx={{fontWeight: 600, mr: 0.25}}>
                            in docs:
                        </Typography>
                        {visibleMentions.map(text => (
                            <Box
                                key={text}
                                sx={{
                                    px: 0.75, py: 0,
                                    fontSize: '0.67rem', lineHeight: 1.8,
                                    borderRadius: '4px',
                                    bgcolor: 'primary.50',
                                    border: '1px solid',
                                    borderColor: 'primary.100',
                                    color: 'primary.700',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {text}
                            </Box>
                        ))}
                        {hiddenMentions > 0 && (
                            <Typography variant="caption" color="text.disabled">
                                +{hiddenMentions} more
                            </Typography>
                        )}
                    </Stack>
                )}

                {/* Wikidata aliases not already covered by the corpus surface forms */}
                {visibleAliases.length > 0 && (
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{mt: 0.25}}>
                        <Typography variant="caption" color="text.disabled" sx={{fontWeight: 600, mr: 0.25}}>
                            aka:
                        </Typography>
                        {visibleAliases.map(alias => (
                            <Box
                                key={alias}
                                sx={{
                                    px: 0.75, py: 0,
                                    fontSize: '0.67rem', lineHeight: 1.8,
                                    borderRadius: '4px',
                                    bgcolor: 'action.selected',
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {alias}
                            </Box>
                        ))}
                        {hiddenAliases > 0 && (
                            <Typography variant="caption" color="text.disabled">
                                +{hiddenAliases} more
                            </Typography>
                        )}
                    </Stack>
                )}

                {hasReports && typeEntries.length > 0 && (
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{mt: 0.5}}>
                        <ArticleOutlinedIcon sx={{fontSize: 13, color: 'text.disabled'}}/>
                        {typeEntries.map(([rType, cnt], i) => (
                            <React.Fragment key={rType}>
                                {i > 0 && (
                                    <Typography component="span" variant="caption" color="text.disabled">·</Typography>
                                )}
                                <Typography
                                    component="span" variant="caption" fontWeight={700}
                                    sx={{color: `${getReportTypeColor(rType)}.main`}}
                                >
                                    {getReportTypeLabel(rType)}
                                </Typography>
                                <Typography component="span" variant="caption" color="text.secondary">
                                    ×{cnt}
                                </Typography>
                            </React.Fragment>
                        ))}
                    </Stack>
                )}
            </Stack>

            <FreqBadge
                occurrenceCount={entity.occurrenceCount}
                documentCount={entity.documentCount}
                reportCount={entity.reportCount}
            />

            <ManualLinkChip manuallyLinked={entity.manuallyLinked}/>
            <ChartLocalChip chartLocal={entity.chartLocal}/>

            <Tooltip title={getEntityTypeDesc(entity.entityType) || entity.entityType} arrow placement="left">
                <Chip
                    label={getEntityTypeLabel(entity.entityType) || entity.entityType}
                    size="small"
                    color={getEntityTypeChipColor(entity.entityType)}
                    variant="outlined"
                    sx={{flexShrink: 0}}
                />
            </Tooltip>
        </Box>
    );
};

// ─── Mention row ──────────────────────────────────────────────────────────────

const MentionRow = ({mention}) => {
    const pct = mention.confidence != null ? `${Math.round(mention.confidence * 100)}%` : null;
    return (
        <Stack
            direction="row"
            alignItems="flex-start"
            gap={2}
            p={2}
            sx={{'&:hover': {bgcolor: 'action.hover'}}}
        >
            {/* Mention surface text */}
            <Stack sx={{flex: 1, minWidth: 0}}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography fontWeight={600} variant="body2">
                        &ldquo;{mention.mentionText}&rdquo;
                    </Typography>
                    {mention.wasDisambiguated && (
                        <Chip label="disambiguated" size="small" variant="outlined" color="info"/>
                    )}
                    <ManualLinkChip manuallyLinked={mention.manuallyLinked}/>
                    {pct && (
                        <Typography variant="caption" color="text.disabled">
                            {pct} conf.
                        </Typography>
                    )}
                </Stack>

                <Stack direction="row" alignItems="center" gap={0.75} sx={{mt: 0.25}} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                        Entity
                    </Typography>
                    <Typography
                        component={RouterLink}
                        to={`/kb/${mention.wikidataId || String(mention.entityId)}`}
                        variant="caption"
                        fontWeight={700}
                        sx={{color: 'primary.main', textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
                    >
                        {mention.canonicalName}
                    </Typography>
                    {mention.docTitle && (
                        <>
                            <Typography variant="caption" color="text.disabled">·</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{
                                maxWidth: 240,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {mention.docTitle}
                            </Typography>
                        </>
                    )}
                </Stack>
            </Stack>

            {/* Freq badges + type chip */}
            <Stack direction="row" gap={0.75} alignItems="center" flexShrink={0}>
                <FreqBadge
                    occurrenceCount={mention.occurrenceCount}
                    documentCount={mention.distinctDocCount}
                />
                <Tooltip title={getEntityTypeDesc(mention.entityType) || mention.entityType} arrow placement="left">
                    <Chip
                        label={getEntityTypeLabel(mention.entityType) || mention.entityType}
                        size="small"
                        color={getEntityTypeChipColor(mention.entityType)}
                        variant="outlined"
                        sx={{flexShrink: 0}}
                    />
                </Tooltip>
            </Stack>
        </Stack>
    );
};

// ─── Grouped mention row ──────────────────────────────────────────────────────

const GroupedMentionRow = ({group}) => {
    const entityPath = group.topEntityRef ? `/kb/${group.topEntityRef}`
        : group.topEntityId ? `/kb/D${group.topEntityId}`
        : null;
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={2}
            p={2}
            sx={{'&:hover': {bgcolor: 'action.hover'}}}
        >
            <Stack sx={{flex: 1, minWidth: 0}}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography fontWeight={700} variant="body2">
                        &ldquo;{group.mentionText}&rdquo;
                    </Typography>
                    {group.distinctEntityCount > 1 && (
                        <Tooltip
                            title={`${group.distinctEntityCount} distinct entity records share this surface form`}
                            arrow placement="top"
                        >
                            <Chip
                                label={`${group.distinctEntityCount} entities`}
                                size="small"
                                variant="outlined"
                                color="warning"
                            />
                        </Tooltip>
                    )}
                    <ManualLinkChip manuallyLinked={group.manuallyLinked}/>
                </Stack>
                {group.topCanonicalName && entityPath && (
                    <Stack direction="row" alignItems="center" gap={0.75} sx={{mt: 0.25}}>
                        <Typography variant="caption" color="text.secondary">Top entity:</Typography>
                        <Typography
                            component={RouterLink}
                            to={entityPath}
                            variant="caption"
                            fontWeight={700}
                            sx={{color: 'primary.main', textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
                        >
                            {group.topCanonicalName}
                        </Typography>
                    </Stack>
                )}
            </Stack>

            <Stack direction="row" gap={0.75} alignItems="center" flexShrink={0}>
                <FreqBadge
                    occurrenceCount={group.totalCount}
                    documentCount={group.distinctDocCount}
                />
                <Tooltip title={getEntityTypeDesc(group.entityType) || group.entityType} arrow placement="left">
                    <Chip
                        label={getEntityTypeLabel(group.entityType) || group.entityType}
                        size="small"
                        color={getEntityTypeChipColor(group.entityType)}
                        variant="outlined"
                        sx={{flexShrink: 0}}
                    />
                </Tooltip>
            </Stack>
        </Stack>
    );
};

// ─── Generic list shell ───────────────────────────────────────────────────────

const ListShell = ({loading, error, empty, children}) => {
    if (loading) return <CircularProgress sx={{alignSelf: 'center', mt: 4}}/>;
    if (error)   return <Typography color="error">Failed to load data.</Typography>;
    if (empty)   return <Typography color="text.secondary">No items found.</Typography>;
    return (
        <Stack borderRadius={2} border="1px solid" borderColor="grey.300">
            {children}
        </Stack>
    );
};

// ─── Approved tab ─────────────────────────────────────────────────────────────

const ApprovedTab = ({onTotalLoaded}) => {
    const [search, setSearch] = useState('');
    const [type, setType]     = useState('');
    const [page, setPage]     = useState(0);
    const dSearch = useDebounce(search, 300);
    const src = useSourceFilters();

    const {entities, loading, error, totalElements, totalPages, entityTypes} = useEntities({
        search: dSearch, entityType: type, tier: 'approved', page, pageSize: DEFAULT_PAGE_SIZE,
        docSourceType: src.docSourceType, inDataItems: src.inDataItems,
        inReports: src.inReports, inCorpus: src.inCorpus,
    });

    useEffect(() => { onTotalLoaded(totalElements); }, [totalElements, onTotalLoaded]);

    const handleSearch = (v) => { setSearch(v); setPage(0); };
    const handleType   = (v) => { setType(v);   setPage(0); };
    const srcHandlersWithPage = {
        ...src.handlers,
        onDocSourceType: (v) => { src.handlers.onDocSourceType(v); setPage(0); },
        onInDataItems:   () => { src.handlers.onInDataItems();      setPage(0); },
        onInReports:     () => { src.handlers.onInReports();        setPage(0); },
        onInCorpus:      () => { src.handlers.onInCorpus();         setPage(0); },
        onReset:         () => { src.handlers.onReset();            setPage(0); },
    };

    return (
        <Stack spacing={2}>
            <EntityFilterBar
                search={search} onSearchChange={handleSearch}
                selectedType={type} onTypeChange={handleType}
                entityTypes={entityTypes}
            />
            <SourcesFilterBar {...src} {...srcHandlersWithPage}/>
            <ListShell loading={loading} error={error} empty={entities.length === 0}>
                {entities.map((e, idx) => (
                    <React.Fragment key={e.listKey}>
                        <EntityRow entity={e}/>
                        {idx < entities.length - 1 && <Divider/>}
                    </React.Fragment>
                ))}
            </ListShell>
            <ListPaginator
                page={page} totalPages={totalPages} totalElements={totalElements}
                pageSize={DEFAULT_PAGE_SIZE} onChange={setPage} loading={loading}
            />
        </Stack>
    );
};

// ─── Candidates tab ───────────────────────────────────────────────────────────

const CandidatesTab = ({onTotalLoaded}) => {
    const [search, setSearch] = useState('');
    const [type, setType]     = useState('');
    const [page, setPage]     = useState(0);
    const dSearch = useDebounce(search, 300);
    const src = useSourceFilters();

    const {entities, loading, error, totalElements, totalPages, entityTypes} = useEntities({
        search: dSearch, entityType: type, tier: 'candidates', page, pageSize: DEFAULT_PAGE_SIZE,
        docSourceType: src.docSourceType, inDataItems: src.inDataItems,
        inReports: src.inReports, inCorpus: src.inCorpus,
    });

    useEffect(() => { onTotalLoaded(totalElements); }, [totalElements, onTotalLoaded]);

    const handleSearch = (v) => { setSearch(v); setPage(0); };
    const handleType   = (v) => { setType(v);   setPage(0); };
    const srcHandlersWithPage = {
        ...src.handlers,
        onDocSourceType: (v) => { src.handlers.onDocSourceType(v); setPage(0); },
        onInDataItems:   () => { src.handlers.onInDataItems();      setPage(0); },
        onInReports:     () => { src.handlers.onInReports();        setPage(0); },
        onInCorpus:      () => { src.handlers.onInCorpus();         setPage(0); },
        onReset:         () => { src.handlers.onReset();            setPage(0); },
    };

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Entities tagged in <strong>draft reports</strong> via the claims mechanism.
                Draft-claim entities move to Approved once the report is published.
                Custom chart entities (D refs) appear in Approved.
            </Typography>
            <EntityFilterBar
                search={search} onSearchChange={handleSearch}
                selectedType={type} onTypeChange={handleType}
                entityTypes={entityTypes}
            />
            <SourcesFilterBar {...src} {...srcHandlersWithPage}/>
            <ListShell loading={loading} error={error} empty={entities.length === 0}>
                {entities.map((e, idx) => (
                    <React.Fragment key={e.listKey}>
                        <EntityRow entity={e}/>
                        {idx < entities.length - 1 && <Divider/>}
                    </React.Fragment>
                ))}
            </ListShell>
            <ListPaginator
                page={page} totalPages={totalPages} totalElements={totalElements}
                pageSize={DEFAULT_PAGE_SIZE} onChange={setPage} loading={loading}
            />
        </Stack>
    );
};

// ─── Customer Entities tab (no Wikidata QID) ──────────────────────────────────

const CustomerEntitiesTab = ({onTotalLoaded}) => {
    const [search, setSearch] = useState('');
    const [type, setType]     = useState('');
    const [page, setPage]     = useState(0);
    const dSearch = useDebounce(search, 300);
    const src = useSourceFilters();

    const {entities, loading, error, totalElements, totalPages, entityTypes} = useEntities({
        search: dSearch, entityType: type, noWikidata: true, page, pageSize: DEFAULT_PAGE_SIZE,
        docSourceType: src.docSourceType, inDataItems: src.inDataItems,
        inReports: src.inReports, inCorpus: src.inCorpus,
    });

    useEffect(() => { onTotalLoaded(totalElements); }, [totalElements, onTotalLoaded]);

    const handleSearch = (v) => { setSearch(v); setPage(0); };
    const handleType   = (v) => { setType(v);   setPage(0); };
    const srcHandlersWithPage = {
        ...src.handlers,
        onDocSourceType: (v) => { src.handlers.onDocSourceType(v); setPage(0); },
        onInDataItems:   () => { src.handlers.onInDataItems();      setPage(0); },
        onInReports:     () => { src.handlers.onInReports();        setPage(0); },
        onInCorpus:      () => { src.handlers.onInCorpus();         setPage(0); },
        onReset:         () => { src.handlers.onReset();            setPage(0); },
    };

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Entities with a <strong>Minsky D-id</strong> — created from customer data and
                not linked to any Wikidata QID. These are private, organisation-specific
                knowledge base entries.
            </Typography>
            <EntityFilterBar
                search={search} onSearchChange={handleSearch}
                selectedType={type} onTypeChange={handleType}
                entityTypes={entityTypes}
            />
            <SourcesFilterBar {...src} {...srcHandlersWithPage}/>
            <ListShell loading={loading} error={error} empty={entities.length === 0}>
                {entities.map((e, idx) => (
                    <React.Fragment key={e.listKey}>
                        <EntityRow entity={e}/>
                        {idx < entities.length - 1 && <Divider/>}
                    </React.Fragment>
                ))}
            </ListShell>
            <ListPaginator
                page={page} totalPages={totalPages} totalElements={totalElements}
                pageSize={DEFAULT_PAGE_SIZE} onChange={setPage} loading={loading}
            />
        </Stack>
    );
};

// ─── Mentions tab ─────────────────────────────────────────────────────────────

const MentionsTab = ({onTotalLoaded}) => {
    const [search, setSearch]       = useState('');
    const [type, setType]           = useState('');
    const [page, setPage]           = useState(0);
    const [grouped, setGrouped]     = useState(true);
    const dSearch = useDebounce(search, 300);
    const src = useSourceFilters();

    const groupedResult = useGroupedMentions({
        search: dSearch, entityType: type, page, pageSize: DEFAULT_GROUPED_MENTIONS_PAGE_SIZE,
        docSourceType: src.docSourceType, inDataItems: src.inDataItems, inCorpus: src.inCorpus,
    });
    const rawResult = useEntityMentionsList({
        search: dSearch, entityType: type, page, pageSize: DEFAULT_MENTIONS_PAGE_SIZE,
        docSourceType: src.docSourceType, inDataItems: src.inDataItems, inCorpus: src.inCorpus,
    });

    const {loading, error, totalElements, totalPages} = grouped ? groupedResult : rawResult;

    useEffect(() => { onTotalLoaded(totalElements); }, [totalElements, onTotalLoaded]);

    const handleSearch  = (v) => { setSearch(v);  setPage(0); };
    const handleType    = (v) => { setType(v);    setPage(0); };
    const handleGrouped = (v) => { setGrouped(v); setPage(0); };
    const srcHandlersWithPage = {
        ...src.handlers,
        onDocSourceType: (v) => { src.handlers.onDocSourceType(v); setPage(0); },
        onInDataItems:   () => { src.handlers.onInDataItems();      setPage(0); },
        onInReports:     () => { src.handlers.onInReports();        setPage(0); },
        onInCorpus:      () => { src.handlers.onInCorpus();         setPage(0); },
        onReset:         () => { src.handlers.onReset();            setPage(0); },
    };

    const pageSize = grouped ? DEFAULT_GROUPED_MENTIONS_PAGE_SIZE : DEFAULT_MENTIONS_PAGE_SIZE;

    return (
        <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="body2" color="text.secondary">
                    NER surface spans whose resolved entity has <strong>no Wikidata link</strong> yet.{' '}
                    {grouped
                        ? 'Grouped by surface form, sorted by frequency — highest count = most segments fixed in one remap.'
                        : 'One row per occurrence in a document segment.'}
                </Typography>
                <Stack direction="row" gap={0.75}>
                    <Tooltip title="Group identical surface forms — shows how many segments each name covers" arrow placement="top">
                        <Chip
                            size="small"
                            label="Grouped"
                            variant={grouped ? 'filled' : 'outlined'}
                            color="primary"
                            onClick={() => handleGrouped(true)}
                        />
                    </Tooltip>
                    <Tooltip title="Show every individual occurrence with document context" arrow placement="top">
                        <Chip
                            size="small"
                            label="Individual"
                            variant={!grouped ? 'filled' : 'outlined'}
                            onClick={() => handleGrouped(false)}
                        />
                    </Tooltip>
                </Stack>
            </Stack>

            <EntityFilterBar
                search={search} onSearchChange={handleSearch}
                selectedType={type} onTypeChange={handleType}
                entityTypes={['ORG', 'PERSON', 'GPE', 'LOC', 'EVENT', 'PRODUCT']}
            />
            <SourcesFilterBar {...src} {...srcHandlersWithPage} showInReports={false}/>

            {grouped ? (
                <ListShell loading={groupedResult.loading} error={groupedResult.error} empty={groupedResult.groups.length === 0}>
                    {groupedResult.groups.map((g, idx) => (
                        <React.Fragment key={`${g.mentionText}|${g.entityType}`}>
                            <GroupedMentionRow group={g}/>
                            {idx < groupedResult.groups.length - 1 && <Divider/>}
                        </React.Fragment>
                    ))}
                </ListShell>
            ) : (
                <ListShell loading={rawResult.loading} error={rawResult.error} empty={rawResult.mentions.length === 0}>
                    {rawResult.mentions.map((m, idx) => (
                        <React.Fragment key={m.mentionId}>
                            <MentionRow mention={m}/>
                            {idx < rawResult.mentions.length - 1 && <Divider/>}
                        </React.Fragment>
                    ))}
                </ListShell>
            )}

            <ListPaginator
                page={page} totalPages={totalPages} totalElements={totalElements}
                pageSize={pageSize} onChange={setPage} loading={loading}
            />
        </Stack>
    );
};

// ─── Root list page ───────────────────────────────────────────────────────────

const EntitiesList = () => {
    const location  = useLocation();
    const navigate  = useNavigate();
    const [tabIdx, setTabIdx] = useState(() => tabIndexFromHash(location.hash));
    const [createOpen, setCreateOpen] = useState(false);

    // Pre-load all three tier totals on mount so badges are visible immediately.
    const tierCounts = useEntityTierCounts();

    // Live totals — seeded from tierCounts, then overridden per-tab when filters are active.
    const [totals, setTotals] = useState([0, 0, 0, 0]);

    useEffect(() => {
        if (!tierCounts.loading) {
            setTotals([tierCounts.approved, tierCounts.candidates, tierCounts.mentions, tierCounts.noQid]);
        }
    }, [tierCounts.loading, tierCounts.approved, tierCounts.candidates, tierCounts.mentions, tierCounts.noQid]);

    const handleTabChange = (_, newIdx) => {
        setTabIdx(newIdx);
        navigate({hash: TABS[newIdx].id}, {replace: true});
    };

    const onApprovedTotal       = useCallback((count) => setTotals(prev => { const n=[...prev]; n[0]=count; return n; }), []);
    const onCandidatesTotal     = useCallback((count) => setTotals(prev => { const n=[...prev]; n[1]=count; return n; }), []);
    const onMentionsTotal       = useCallback((count) => setTotals(prev => { const n=[...prev]; n[2]=count; return n; }), []);
    const onCustomerEntitiesTotal = useCallback((count) => setTotals(prev => { const n=[...prev]; n[3]=count; return n; }), []);

    const handleCustomEntityCreated = useCallback((entity) => {
        const ref = entity?.id || entity?.wikidataId || entity?.entityRef;
        if (ref) {
            navigate(`/kb/${encodeURIComponent(ref)}`);
        }
    }, [navigate]);

    return (
        <Stack p={2} spacing={2} sx={{height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Typography variant="h5" fontWeight={600}>Knowledge Base</Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={() => setCreateOpen(true)}
                >
                    Create custom entity
                </Button>
            </Stack>

            <Box sx={{borderBottom: 1, borderColor: 'divider', flexShrink: 0}}>
                <Tabs value={tabIdx} onChange={handleTabChange} aria-label="KB entity tiers">
                    {TABS.map((t, i) => (
                        <Tab
                            key={t.id}
                            icon={t.icon}
                            iconPosition="start"
                            label={
                                <span>
                                    {t.label}
                                    <TabCount count={totals[i]} loading={tierCounts.loading}/>
                                </span>
                            }
                        />
                    ))}
                </Tabs>
            </Box>

            {/* All four tabs stay mounted; only the active one is visible.
                This prevents height jumps when switching tabs. */}
            <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', position: 'relative'}}>
                <Box sx={{display: tabIdx === 0 ? 'block' : 'none', p: 2}}>
                    <ApprovedTab         onTotalLoaded={onApprovedTotal}/>
                </Box>
                <Box sx={{display: tabIdx === 1 ? 'block' : 'none', p: 2}}>
                    <CandidatesTab       onTotalLoaded={onCandidatesTotal}/>
                </Box>
                <Box sx={{display: tabIdx === 2 ? 'block' : 'none', p: 2}}>
                    <MentionsTab         onTotalLoaded={onMentionsTotal}/>
                </Box>
                <Box sx={{display: tabIdx === 3 ? 'block' : 'none', p: 2}}>
                    <CustomerEntitiesTab onTotalLoaded={onCustomerEntitiesTotal}/>
                </Box>
            </Box>

            <CreateCustomEntityDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSuccess={handleCustomEntityCreated}
            />
        </Stack>
    );
};

export default EntitiesList;
export {ManualLinkChip, ChartLocalChip, EntityRow, MentionRow, GroupedMentionRow, ListPaginator};
